import { useRef, useState } from 'react';
import { AlertCircle, Check, Upload } from 'lucide-react';
import { MEDIA_UPLOAD_MAX_LABEL } from '@mg/shared';
import { Modal } from '../../components/ui/Modal';
import { Button } from '../../components/ui/Button';
import { Spinner } from '../../components/ui/Spinner';
import { useDialogs } from '../../components/ui/DialogProvider';
import { cn } from '../../lib/cn';
import { formatBytes } from './mediaDisplay';
import { MediaThumb } from './MediaThumb';
import type { UploadItem } from './useMediaUploads';

/** What the native picker offers, matching the server's allowlist: three prefixes and the PDF. */
const UPLOAD_ACCEPT = 'image/*,video/*,audio/*,application/pdf';

/**
 * Upload media.
 *
 * Replaces going straight to the native file picker. The picker gave no room to say what is
 * accepted, no progress on a 90 MB video, and no way to report that one file of five was rejected —
 * it either worked or an alert appeared afterwards. This is the same act with somewhere to put that
 * information.
 *
 * Closing does not cancel: the queue is owned by the page, so uploads carry on and the Upload button
 * shows what is still running. Only Cancel discards, and only after asking.
 */
export function UploadMediaModal({
  open,
  onClose,
  items,
  onAdd,
  onCancelInFlight,
  uploading,
}: {
  open: boolean;
  /** Dismiss. In-flight uploads keep going — see the note above. */
  onClose: () => void;
  items: UploadItem[];
  onAdd: (files: File[]) => void;
  onCancelInFlight: () => void;
  uploading: number;
}) {
  const { confirm } = useDialogs();
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragOver, setDragOver] = useState(false);

  const cancel = async () => {
    // Nothing in flight is nothing to lose — asking would be a dialog about no consequence.
    if (uploading > 0) {
      const ok = await confirm({
        title: `Stop ${uploading} upload${uploading === 1 ? '' : 's'}?`,
        message:
          uploading === 1
            ? 'The file still uploading will be discarded. Anything already uploaded stays in your library.'
            : 'Files still uploading will be discarded. Anything already uploaded stays in your library.',
        confirmLabel: 'Discard',
        danger: true,
      });
      if (!ok) return;
      onCancelInFlight();
    }
    onClose();
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Upload media"
      footer={
        <>
          <Button variant="secondary" onClick={() => void cancel()}>
            Cancel
          </Button>
          {/*
            Always enabled, and it simply closes. Gating it on "at least one finished" would sit a
            disabled button in front of someone whose only remaining task is to wait — and waiting is
            exactly what they do not have to do here, because the uploads outlive this dialog.
          */}
          <Button onClick={onClose}>Done</Button>
        </>
      }
    >
      <div className="space-y-4">
        <label
          onDragOver={(e) => {
            e.preventDefault();
            setDragOver(true);
          }}
          onDragLeave={() => setDragOver(false)}
          onDrop={(e) => {
            e.preventDefault();
            setDragOver(false);
            onAdd(Array.from(e.dataTransfer.files));
          }}
          className={cn(
            'flex cursor-pointer flex-col items-center justify-center gap-3 rounded-xl border-2 border-dashed px-6 py-10 text-center transition-colors',
            dragOver ? 'border-action bg-tint' : 'border-line-dashed bg-page/40',
          )}
        >
          <span className="flex h-12 w-12 items-center justify-center rounded-full bg-tint text-tint-fg">
            <Upload className="h-5 w-5" aria-hidden />
          </span>
          <span className="text-base font-semibold text-ink">Drag and drop files here</span>
          <span className="text-sm text-ink-muted">
            Images, video, audio and PDFs &middot; up to {MEDIA_UPLOAD_MAX_LABEL} each
          </span>
          <Button variant="secondary" size="sm" onClick={() => inputRef.current?.click()}>
            Browse files
          </Button>
          <input
            ref={inputRef}
            type="file"
            accept={UPLOAD_ACCEPT}
            multiple
            className="hidden"
            onChange={(e) => {
              onAdd(Array.from(e.target.files ?? []));
              // Cleared so picking the SAME file again still fires a change event.
              e.target.value = '';
            }}
          />
        </label>

        {items.length > 0 && (
          <ul className="space-y-2">
            {items.map((item) => (
              <UploadRow key={item.id} item={item} />
            ))}
          </ul>
        )}
      </div>
    </Modal>
  );
}

function UploadRow({ item }: { item: UploadItem }) {
  const failed = item.status === 'error';
  const percent = item.progress === null ? null : Math.round(item.progress * 100);

  return (
    <li
      className={cn(
        'flex items-center gap-3 rounded-lg border px-3 py-2.5',
        failed ? 'border-red-200 dark:border-error-dark/30 bg-red-50/60 dark:bg-error-dark-tint/60' : 'border-line',
      )}
    >
      <span className="flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-md">
        {failed ? (
          <span className="flex h-full w-full items-center justify-center rounded-md bg-red-100 dark:bg-error-dark-tint text-red-500 dark:text-error-dark">
            <AlertCircle className="h-5 w-5" aria-hidden />
          </span>
        ) : item.asset ? (
          // The real thumbnail once it exists — which for a video is the still frame captured on
          // the way up, so the row shows the same image the grid is about to.
          <MediaThumb asset={item.asset} className="h-full w-full rounded-md" />
        ) : (
          <span className="flex h-full w-full items-center justify-center rounded-md bg-chip">
            <Spinner className="h-4 w-4 text-action" />
          </span>
        )}
      </span>

      <span className="min-w-0 flex-1">
        {/* Filenames are long and the middle is rarely the useful part; truncating keeps the row
            one line without hiding the extension the user is scanning for. */}
        <span className="block truncate text-sm font-semibold text-ink">{item.name}</span>
        {failed ? (
          <span className="block text-xs text-red-600 dark:text-error-dark">{item.error}</span>
        ) : (
          <span className="block text-xs text-ink-muted">
            {formatBytes(item.sizeBytes)} &middot;{' '}
            {item.status === 'done'
              ? 'uploaded'
              : percent === null
                ? 'uploading'
                : `uploading ${percent}%`}
          </span>
        )}
        {item.status === 'uploading' && (
          <span className="mt-1.5 block h-1 w-full overflow-hidden rounded-full bg-line">
            <span
              className={cn(
                'block h-full rounded-full bg-action transition-[width] duration-200',
                // No measurable total: keep the bar moving rather than parking it at 0%, which
                // reads as stalled.
                percent === null && 'animate-pulse w-1/3',
              )}
              style={percent === null ? undefined : { width: `${percent}%` }}
            />
          </span>
        )}
      </span>

      {item.status === 'done' && (
        <Check className="h-5 w-5 shrink-0 text-success dark:text-success-dark" aria-label="Uploaded" />
      )}
    </li>
  );
}
